/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable no-console */
import { ProductName } from '@pypestream/api-services';
import { i18n } from '@pypestream/translations';
import { html, nothing, PropertyValueMap, unsafeCSS } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { property, state } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { isEqual } from 'lodash';

import { logos } from '../../assets/logos';
import megaMenuEmptyStateIcon from '../../assets/mega-menu-placeholder.svg?url';
import type { MenuItemWC } from '../../components';
import {
  BaseElement,
  customElement,
  watch,
} from '../../components/base-element';
import { Translate } from '../../components/base-element/mixins/translation-mixin';
import { scrollIntoView } from '../../components/base-element/utilities/scroll';
import { ScrollbarWC } from '../../components/scrollbar/scrollbar.wc';
import '../../components/text/text-subtitle.wc';
import { CommonProject } from '../universal-nav/types';
import { smartService } from '../xstate/smart.xstate';
import { SmartContext } from '../xstate/smart.xstate-utils';

import styles from './nav-breadcrumb.scss?inline';

interface NavBreadcrumbState {
  projects: CommonProject[];
  // selected project by mega menu pov
  selectedProject?: CommonProject;
  // Actual viewing project i.e. project representing this.projectId
  viewingProject?: CommonProject;
}

@customElement('ps-nav-breadcrumb')
export class NavBreadcrumbWC extends Translate(BaseElement) {
  static styles = unsafeCSS(styles);

  context: SmartContext;

  @watch('lang')
  async onLangChanged() {
    // @ts-ignore
    await this.reloadLanguage();
  }

  @property({ reflect: false }) projectId: string;

  @property({ reflect: false, type: Object }) selectedOrg: {
    name: string;
    id: string;
    icon?: string;
  } = {
    id: '',
    name: '',
  };

  @property({ reflect: false, type: Boolean }) isDisabledOrgs?: boolean = false;

  @property({ reflect: true }) app: ProductName = ProductName.Organization;

  @property({ reflect: false, type: Boolean }) hideGoHome?: boolean = false;

  @state() private navBreadcrumbState: NavBreadcrumbState = {
    projects: [],
  };

  @state() onProjectSelect: (projectId: string | undefined) => void;

  @state() onSelectionChange: (org: string | null) => void;

  @property() onManagerToolClick?: (route: string) => void;

  @state() private isLoadingProjects: boolean = false;

  @state() private hasLoadingProjectsError: boolean = false;

  @watch('project', { waitUntilFirstUpdate: true })
  handleResetSelectedProject() {
    this.navBreadcrumbState.selectedProject =
      this.navBreadcrumbState.projects.find(
        ({ project: { id } }) => id === this.projectId
      );
  }

  private chatTimerId: NodeJS.Timeout;

  constructor() {
    super();

    this.init = this.init.bind(this);
  }

  async connectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.connectedCallback();

    smartService.subscribe((smartState) => {
      const currentUserInfo = this.context?.userInfo;
      const assignedProjects = this.context?.assignedProjects || [];

      // respect subscription updates and request for UI rerender.
      if (
        assignedProjects &&
        !isEqual(this.navBreadcrumbState.projects, assignedProjects)
      ) {
        this.navBreadcrumbState.projects = assignedProjects;

        if (this.navBreadcrumbState.selectedProject) {
          const selectedProjectIndex = assignedProjects.findIndex(
            (project) =>
              project.project.id ===
              this.navBreadcrumbState.selectedProject?.project.id
          );

          if (selectedProjectIndex >= 0) {
            this.navBreadcrumbState.selectedProject.availableProducts =
              assignedProjects[selectedProjectIndex].availableProducts;
          } else {
            this.navBreadcrumbState.selectedProject = undefined;
          }
        }
        this.requestUpdate();
      }

      this.context = smartState.context;

      if (this.context.userInfo?.defaultAccount) {
        const {
          defaultAccount: { allChildAccounts = [], ...defaultAccountDetails },
        } = this.context.userInfo;

        const selectChildOrg = [
          ...allChildAccounts,
          defaultAccountDetails,
        ].find((_childAcc) => _childAcc.id === smartState.context.accountId);

        if (selectChildOrg) {
          this.selectedOrg.name = selectChildOrg.name || '';
          this.selectedOrg.id = selectChildOrg.id || '';
          this.selectedOrg.icon = selectChildOrg.pictureFile?.url;
        }
      }
      this.isLoadingProjects =
        smartState.matches('projects.loading') ||
        (!assignedProjects?.length && smartState.matches('userInfo.loading'));

      this.hasLoadingProjectsError = smartState.matches('projects.loadError');

      if (
        currentUserInfo !== smartState.context.userInfo ||
        assignedProjects !== smartState.context.assignedProjects
      ) {
        this.init();
      }
    });
  }

  protected updated(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>
  ): void {
    if (changedProperties.has('env') || changedProperties.has('orgId')) {
      this.init();
    }
  }

  disconnectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.disconnectedCallback();

    clearTimeout(this.chatTimerId);
  }

  init() {
    const { assignedProjects, userInfo } = this.context;

    if (assignedProjects) {
      this.navBreadcrumbState.projects = assignedProjects || [];
      this.navBreadcrumbState.selectedProject =
        // @todo - check this
        this.navBreadcrumbState.projects.length > 0
          ? this.navBreadcrumbState.projects.find(
              ({ project: { id } }) => id === this.projectId
            )
          : undefined;

      this.navBreadcrumbState.viewingProject =
        this.navBreadcrumbState.selectedProject;
      this.requestUpdate();
      this.handleClickMegaMenuIcon();
    }

    if (userInfo?.settings?.defaultLanguage) {
      this.lang = userInfo.settings.defaultLanguage.split('-')[0];
    }
  }

  // Auto-scroll to the selected item.
  // eslint-disable-next-line class-methods-use-this
  handleClickMegaMenuIcon() {
    setTimeout(() => {
      // target the popover itself vs whatever is initially rendered WITHIN this component --> needs to work with new portal placement
      const popoverElem = document.querySelector('#megamenu-popover');

      if (popoverElem) {
        const selectedItems =
          popoverElem.querySelectorAll<MenuItemWC>('ps-menu-item');
        const selectedItem = Array.from(selectedItems).filter(
          (item) => item.selected === true
        );

        const scrollBarContainer = popoverElem.querySelector(
          '.c-mega-menu__scrollbar'
        ) as ScrollbarWC;

        const scrollableContainer =
          scrollBarContainer?.simplebar?.getScrollElement();

        if (selectedItem[0] && scrollableContainer) {
          scrollIntoView(
            selectedItem[0],
            scrollableContainer,
            'vertical',
            'auto'
          );
        }
      }
    }, 0);
  }

  handleManagerToolClick(route?: string) {
    if (!route || !this.onManagerToolClick) return;
    // To avoid bug that mega menu still open after clicking and during route reloading
    document
      .querySelector('ps-universal-nav')
      ?.shadowRoot?.querySelector('ps-mega-menu')
      ?.shadowRoot?.querySelector('#megamenu-trigger')
      ?.dispatchEvent(new Event('click', { bubbles: true }));
    this.onManagerToolClick(route);
  }

  getAppLogoName(): keyof typeof logos | undefined {
    switch (this.app) {
      case ProductName.Organization:
        return 'manager';
      case ProductName.AgentAssist:
        return 'agentAssist';
      case ProductName.Analytics:
        return 'analytics';
      case ProductName.ProStudio:
        return 'proStudio';
      default:
        return 'manager';
    }
  }

  renderTools() {
    if (!this.navBreadcrumbState.selectedProject)
      return html`
        <div class="c-mega-menu__tools-placeholder">
          <ps-image
            src=${megaMenuEmptyStateIcon}
            width="271px"
            height="82px"
          ></ps-image>
          <ps-text-subtitle
            size="xsmall"
            variant="tertiary"
            textAlign="center"
            class="c-mega-menu__tools-placeholder-label"
          >
            ${i18n.t('navigation:mega.content.empty.part1') || 'Select a'}
            <span class="c-mega-menu__project-word-color"
              >${i18n.t('navigation:mega.content.empty.part2') ||
              'Project'}</span
            >
            ${i18n.t('navigation:mega.content.empty.part3') ||
            'from the menu to get started'}
          </ps-text-subtitle>
        </div>
      `;

    return html`
      <ps-spacer></ps-spacer>
      <ps-stack gutter="large">
        ${map(
          this.navBreadcrumbState.selectedProject?.availableProducts || [],
          (product) => html`
            <ps-card
              test-id=${`mega-menu-product-cta-${product.label?.replace(' ', '-').toLowerCase()}`}
              class="c-mega-menu__tools-item"
              size="large"
              interactive
              ?disabled=${product.disabled}
              .href=${product.name === ProductName.Organization &&
              this.onManagerToolClick
                ? undefined
                : product.url}
              @click=${product.name === ProductName.Organization &&
              this.onManagerToolClick
                ? () => this.handleManagerToolClick(product.url)
                : undefined}
            >
              <ps-stack gutter="xsmall" direction="column" alignItems="center">
                <ps-image
                  src=${logos[product.logo]}
                  width="40px"
                  height="40px"
                ></ps-image>
                <div style="margin-inline: -22px;">
                  <ps-text-caption variant="primary" truncate textAlign="center"
                    >${product.label}</ps-text-caption
                  >
                </div>
              </ps-stack>
            </ps-card>
          `
        )}
      </ps-stack>
    `;
  }

  handleProjectSelect(event: MouseEvent, project: CommonProject) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const selectedProject =
      this.navBreadcrumbState.selectedProject?.project.id === project.project.id
        ? undefined
        : project;

    this.navBreadcrumbState.selectedProject = selectedProject;
    this.requestUpdate();
    this.onProjectSelect?.(selectedProject?.project.id);
  }

  renderProjects() {
    const { projects, selectedProject } = this.navBreadcrumbState;
    if (this.hasLoadingProjectsError) {
      return html`
        <ps-text-body size="small" variant="tertiary">
          ${i18n.t('navigation:mega.sidebar.error') ||
          'Somethings went wrong. Please reload page.'}
        </ps-text-body>
      `;
    }

    if (this.isLoadingProjects) {
      return html`
        <div class="c-mega-menu__projects-loader">
          <ps-spinner></ps-spinner>
        </div>
      `;
    }

    if (projects.length) {
      return html` <div class="c-mega-menu__scrollbar-wrapper">
        <ps-scrollbar
          class="c-mega-menu__scrollbar"
          force-visible
          disable-auto-hide
        >
          <ps-menu class="c-mega-menu__projects-list">
            ${map(
              projects,
              (project) => html`
                <ps-menu-item
                  ?selected=${selectedProject?.project.id ===
                  project.project.id}
                  @click=${(event: MouseEvent) =>
                    this.handleProjectSelect(event, project)}
                >
                  ${project?.project.name}
                </ps-menu-item>
              `
            )}
          </ps-menu>
        </ps-scrollbar>
      </div>`;
    }

    return html`
      <ps-text-body size="small" variant="tertiary">
        ${i18n.t('navigation:mega.sidebar.empty') || 'No available projects'}
      </ps-text-body>
    `;
  }

  renderProjectButtonLabel() {
    const appLogoName = this.getAppLogoName();
    const appLogo = html`<ps-image
      src=${ifDefined(appLogoName && logos[appLogoName])}
      width="32px"
      height="32px"
      slot="suffix"
      style="margin-inline-start: -6px;"
    ></ps-image>`;

    if (
      this.navBreadcrumbState.viewingProject &&
      !!this.app &&
      this.app !== ProductName.Organization
    ) {
      return html` ${(this.navBreadcrumbState.viewingProject.project.files
          .length > 0 &&
          html`<ps-image
            src=${this.navBreadcrumbState.viewingProject.project.files[0].url}
            width="32px"
            height="32px"
            slot="prefix"
            radius="small"
          >
            <slot slot="fallbackImage">
              <ps-avatar
                size="medium"
                label=${`${this.navBreadcrumbState.viewingProject.project.name?.split(' ')[0]}`}
                shape="square"
                slot="prefix"
              ></ps-avatar>
            </slot>
          </ps-image>`) ||
        nothing}
        <span style="max-width: 150px; display: inline-block;">
          <ps-text-subtitle truncate variant="secondary" size="small"
            >${this.navBreadcrumbState.viewingProject.project
              .name}</ps-text-subtitle
          ></span
        >${appLogo}`;
    }

    return html`<ps-image
      src=${ifDefined(appLogoName && logos[appLogoName])}
      width="32px"
      height="32px"
    ></ps-image>`;
  }

  renderProjectSelector() {
    return html`
      <ps-icon name="slash" size="xsmall" color="gray"></ps-icon>
      <ps-button
        variant="ghost"
        id="megamenu-trigger"
        @click=${this.handleClickMegaMenuIcon}
        size="large"
        class="c-mega-menu-trigger"
      >
        ${this.renderProjectButtonLabel()}
        <ps-icon slot="suffix" name="chevrons-up-down" size="xsmall"></ps-icon>
      </ps-button>
      <ps-popover
        id="megamenu-popover"
        trigger="megamenu-trigger"
        placement="bottom-start"
        width="auto"
        size="none"
        .hasArrow=${false}
        .omitPortal=${true}
        skidding="0"
        class="c-mega-menu-popover"
      >
        <div class="c-mega-menu">
          <div class="c-mega-menu__projects">
            <ps-text-subtitle size="xsmall" variant="tertiary">
              ${i18n.t('navigation:mega.sidebar.title') || 'Projects'}
            </ps-text-subtitle>
            <ps-spacer size="small"></ps-spacer>
            ${this.renderProjects()}
          </div>

          <div class="c-mega-menu__tools">
            <ps-text-subtitle size="xsmall" variant="tertiary">
              ${i18n.t('navigation:mega.content.title') || 'Tools'}
            </ps-text-subtitle>
            ${this.renderTools()}
          </div>
        </div>
      </ps-popover>
    `;
  }

  renderOrgSelector() {
    const isManagerApp = this.app === ProductName.Organization;

    const defaultOrgFallbackImgEle = html`<ps-avatar
      size="medium"
      label=${`${this.selectedOrg.name?.split(' ')[0]}`}
      shape="square"
      slot="prefix"
    ></ps-avatar>`;

    return html`
      <style>
        .c-mega-menu__org-selector {
          margin-inline-start: -8px;
        }
        .c-mega-menu__org-selector::part(button) {
          padding: 8px;
        }
      </style>
      ${(!isManagerApp &&
        html`<style>
          .c-mega-menu__org-selector::part(prefix) {
            margin-inline-end: 2px;
          }
        </style>`) ||
      nothing}
      <ps-button
        variant="ghost"
        id="org-selector-trigger"
        data-testid="org-selector-trigger"
        size="large"
        class="c-mega-menu__org-selector"
      >
        ${this.selectedOrg.icon
          ? html`<ps-image
              src=${this.selectedOrg.icon}
              width="32px"
              height="32px"
              slot="prefix"
              radius="small"
            >
              <slot name="fallbackImage" slot="fallbackImage">
                ${defaultOrgFallbackImgEle}
              </slot>
            </ps-image>`
          : defaultOrgFallbackImgEle}
        ${isManagerApp
          ? html`<span style="max-width: 150px; display: inline-block;">
              <ps-text-subtitle truncate variant="secondary" size="small"
                >${this.selectedOrg.name}</ps-text-subtitle
              ></span
            >`
          : nothing}
        <ps-icon slot="suffix" name="chevrons-up-down" size="xsmall"></ps-icon>
      </ps-button>
      <ps-popover
        id="org-selector-trigger"
        trigger="org-selector-trigger"
        placement="bottom-start"
        width="default"
        .hasArrow=${false}
        .size=${'small'}
        .omitPortal=${true}
        skidding="0"
      >
        ${this.context?.accountId || this.context?.userInfo?.defaultAccount
          ? html`
              <ps-menu-org-selector
                orgId=${ifDefined(
                  this.context?.accountId ||
                    this.context.userInfo?.defaultAccount
                )}
                .onSelectionChange=${this.onSelectionChange}
                ?isDisabled=${this.isDisabledOrgs}
                .hideGoHome=${this.hideGoHome}
              ></ps-menu-org-selector>
            `
          : undefined}
      </ps-popover>
    `;
  }

  protected render() {
    const showTopLevelBreadcrumbOnly =
      this.hideGoHome && this.app === ProductName.Organization;

    if (this.isDisabledOrgs && !this.selectedOrg.id) {
      return html`<ps-spinner></ps-spinner>`;
    }

    return html`
      <div style="display: flex; align-items: center;">
        ${this.renderOrgSelector()}
        ${!showTopLevelBreadcrumbOnly
          ? html`${this.renderProjectSelector()}
              <!-- slot to render breadcrumb extra content if provided -->
              <slot name="nav-breadcrumb-extra"> </slot>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-nav-breadcrumb': NavBreadcrumbWC;
  }

  enum PSElementTagNameMap {
    'ps-nav-breadcrumb' = 'ps-nav-breadcrumb',
  }
}
